import { animate, stagger } from 'animejs';

function initSingleChatPlatformBadge({ root, button, logo, prefersReduced }) {
  if (!button || !logo) return;

  root.classList.add('is-single');
  root.classList.toggle('is-animated', !prefersReduced);
  root.classList.toggle('is-paused', !!prefersReduced);

  const setEngaged = (value) => {
    root.classList.toggle('is-engaged', value);
  };

  const clearPressedState = () => {
    root.classList.remove('is-pressed');
  };

  button.addEventListener('mouseenter', () => setEngaged(true));
  button.addEventListener('mouseleave', () => {
    setEngaged(false);
    clearPressedState();
  });

  button.addEventListener('focus', () => setEngaged(true));
  button.addEventListener('blur', () => {
    setEngaged(false);
    clearPressedState();
  });

  button.addEventListener('pointerdown', () => {
    root.classList.add('is-pressed');
  });

  button.addEventListener('pointerup', clearPressedState);
  button.addEventListener('pointercancel', clearPressedState);

  document.addEventListener('visibilitychange', () => {
    root.classList.toggle('is-paused', document.hidden || prefersReduced);
  });

  if (prefersReduced) return;

  button.addEventListener('click', () => {
    animate(logo, {
      scale: [1, 1.14, 0.98, 1],
      rotate: [0, -7, 5, 0],
      duration: 560,
      easing: 'outElastic(1, .65)',
    });
  });
}

export function initChatPlatformBadge() {
  const root = document.querySelector('[data-chat-badge]');
  if (!root) return;

  const primaryBtn = root.querySelector('.chat-platform-badge-inner');
  const toggleBtn = root.querySelector('[data-chat-badge-toggle]');
  const menu = root.querySelector('[data-chat-menu]');
  const logos = Array.from(root.querySelectorAll('.chat-logo'));
  const items = Array.from(root.querySelectorAll('.chat-platform-item'));

  const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

  if (root.classList.contains('is-single') || (!toggleBtn && !menu && logos.length === 1)) {
    initSingleChatPlatformBadge({
      root,
      button: primaryBtn,
      logo: logos[0],
      prefersReduced,
    });
    return;
  }

  if (!toggleBtn || !menu || logos.length < 2) return;

  // ---------- Helpers ----------
  function badgePulse() {
    if (prefersReduced) return;

    // little “pop” + glow
    animate(toggleBtn, {
      scale: [1, 1.06, 1],
      duration: 420,
      easing: 'outElastic(1, .6)',
    });

    // optional glow (uses boxShadow animation)
    animate(toggleBtn, {
      boxShadow: [
        '0 4px 10px rgba(0,0,0,.20)',
        '0 10px 24px rgba(0,0,0,.30)',
        '0 4px 10px rgba(0,0,0,.20)'
      ],
      duration: 520,
      easing: 'inOutQuad',
    });
  }

  function wobbleOnClick() {
    if (prefersReduced) return;
    animate(toggleBtn, {
      rotate: [0, -6, 6, -3, 3, 0],
      duration: 520,
      easing: 'easeOutQuad',
    });
  }

  // ---------- Icon cycling ----------
  let idx = 0;
  let timer = null;

  function showLogo(nextIdx) {
    const current = logos[idx];
    const next = logos[nextIdx];
    if (current === next) return;

    next.style.display = 'block';

    if (prefersReduced) {
      current.classList.remove('is-active');
      next.classList.add('is-active');
      current.style.display = 'none';
      idx = nextIdx;
      return;
    }

    // Out: fade + slight rotate
    animate(current, {
      opacity: [1, 0],
      scale: [1, 0.9],
      rotate: [0, -10],
      duration: 240,
      easing: 'inOutQuad',
      complete: () => {
        current.classList.remove('is-active');
        current.style.display = 'none';
        current.style.transform = ''; // cleanup inline transform drift
      }
    });

    // In: fade + small overshoot
    next.classList.add('is-active');
    animate(next, {
      opacity: [0, 1],
      scale: [0.86, 1.04, 1],
      rotate: [10, 0],
      duration: 420,
      easing: 'outElastic(1, .6)',
      complete: () => {
        next.style.transform = ''; // cleanup
      }
    });

    idx = nextIdx;
    badgePulse();
  }

  function startCycling() {
    if (timer || prefersReduced) return;
    timer = window.setInterval(() => {
      const nextIdx = (idx + 1) % logos.length;
      if (!root.classList.contains('is-open')) showLogo(nextIdx);
    }, 2400);
  }

  function stopCycling() {
    if (!timer) return;
    window.clearInterval(timer);
    timer = null;
  }

  // ---------- Menu open/close (more “special”) ----------
  function openMenu() {
    root.classList.add('is-open');
    menu.setAttribute('aria-hidden', 'false');

    if (prefersReduced) {
      items.forEach(el => { el.style.opacity = 1; el.style.transform = 'translateY(0)'; });
      return;
    }

    // badge “pop”
    animate(toggleBtn, {
      scale: [1, 1.08, 1],
      duration: 420,
      easing: 'outElastic(1, .6)',
    });

    // Items: orbit-ish + pop-in
    animate(items, {
      opacity: [0, 1],
      translateY: [12, 0],
      translateX: [8, 0],
      scale: [0.94, 1],
      rotate: [-2, 0],
      duration: 520,
      delay: stagger(70, { from: 'last' }),
      easing: 'outExpo'
    });
  }

  function closeMenu() {
    root.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');

    if (prefersReduced) {
      items.forEach(el => { el.style.opacity = 0; el.style.transform = 'translateY(6px)'; });
      return;
    }

    animate(items, {
      opacity: [1, 0],
      translateY: [0, 10],
      translateX: [0, 6],
      scale: [1, 0.96],
      duration: 220,
      delay: stagger(40, { from: 'first' }),
      easing: 'inQuad'
    });
  }
  // Close menu after choosing an option
  items.forEach((item) => {
    item.addEventListener('click', () => {
      closeMenu();
    });
  });

  // ---------- Events ----------
  toggleBtn.addEventListener('click', () => {
    wobbleOnClick();
    const isOpen = root.classList.contains('is-open');
    if (isOpen) closeMenu();
    else openMenu();
  });

  // Close on outside click
  document.addEventListener('click', (e) => {
    if (!root.classList.contains('is-open')) return;
    if (!root.contains(e.target)) closeMenu();
  });

  // Pause cycling while hovering the badge area (desktop)
  root.addEventListener('mouseenter', stopCycling);
  root.addEventListener('mouseleave', startCycling);

  // Optional: close when pressing ESC
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && root.classList.contains('is-open')) closeMenu();
  });

  startCycling();
}
